import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowLeftIcon,
  ArrowsPointingOutIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import type { ReferenceImage } from '../../client'
import { anchoredStyle } from '../../lib/anchor'
import { MIN_QUERY, useReferenceLibrary } from './useReferenceLibrary'

const MAX_SELECTED = 5
const WIDTH = 288
const HEIGHT = 280

const THEMES = {
  light: {
    panel: 'border-gray-200 bg-white text-gray-700',
    icon: 'text-gray-500 hover:bg-gray-100',
    heading: 'text-gray-700',
    muted: 'text-gray-400',
    divider: 'bg-gray-200',
    tile: 'border-gray-200 hover:border-purple-400',
    input: 'border border-gray-300 bg-white text-gray-700',
  },
  dark: {
    panel: 'border-white/10 bg-gray-900/90 text-white backdrop-blur-md',
    icon: 'text-white/70 hover:bg-white/10',
    heading: 'text-white/70',
    muted: 'text-white/40',
    divider: 'bg-white/10',
    tile: 'border-white/10 hover:border-purple-300',
    input: 'border border-white/10 bg-white/10 text-white placeholder-white/40',
  },
}

type Props = {
  brandId: number
  anchor: DOMRect
  theme: keyof typeof THEMES
  images: ReferenceImage[]
  selected: string[]
  onImagesChange: (images: ReferenceImage[]) => void
  onSelectedChange: (urls: string[]) => void
  onPreview: (url: string) => void
  onClose: () => void
}

export default function ImageSourcePopover({
  brandId,
  anchor,
  theme,
  images,
  selected,
  onImagesChange,
  onSelectedChange,
  onPreview,
  onClose,
}: Props) {
  const [mode, setMode] = useState<'main' | 'pexels'>('main')
  const panelRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const library = useReferenceLibrary({
    brandId,
    images,
    selected,
    maxSelected: MAX_SELECTED,
    onImagesChange,
    onSelectedChange,
  })
  const t = THEMES[theme]

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!panelRef.current?.contains(event.target as Node)) onClose()
    }

    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [onClose])

  const iconButton = `flex size-6 items-center justify-center rounded-md transition disabled:opacity-50 ${t.icon}`

  return createPortal(
    <div
      ref={panelRef}
      data-image-source
      style={anchoredStyle(anchor, { width: WIDTH, height: HEIGHT })}
      className={`fixed z-[210] rounded-xl border shadow-2xl ${t.panel}`}
    >
      {mode === 'main' ? (
        <div className="p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className={`text-xs font-semibold ${t.heading}`}>
              Reference images
              <span className={`ml-1 font-normal ${t.muted}`}>
                {selected.length}/{MAX_SELECTED}
              </span>
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Upload image"
                disabled={library.uploading}
                onClick={() => fileInputRef.current?.click()}
                className={iconButton}
              >
                {library.uploading ? (
                  <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <ArrowUpTrayIcon className="size-3.5" />
                )}
              </button>
              <button type="button" aria-label="Search web" onClick={() => setMode('pexels')} className={iconButton}>
                <MagnifyingGlassIcon className="size-3.5" />
              </button>
              <span className={`mx-0.5 h-4 w-px ${t.divider}`} />
              <button type="button" aria-label="Close" onClick={onClose} className={iconButton}>
                <XMarkIcon className="size-3.5" />
              </button>
            </div>
          </div>

          {images.length > 0 ? (
            <div className="grid max-h-[180px] grid-cols-4 gap-1.5 overflow-y-auto [scrollbar-width:thin]">
              {images.map(({ gcs_url }) => {
                const isSelected = selected.includes(gcs_url)
                return (
                  <div
                    key={gcs_url}
                    onClick={() => library.toggle(gcs_url)}
                    className={`group relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 transition ${
                      isSelected ? 'border-purple-500 ring-2 ring-purple-300/60' : t.tile
                    }`}
                  >
                    <img src={gcs_url} alt="" className="size-full object-cover" />
                    {isSelected && (
                      <>
                        <span className="pointer-events-none absolute inset-0 bg-purple-900/10" />
                        <span className="pointer-events-none absolute right-1 bottom-1 flex size-4 items-center justify-center rounded-full bg-purple-600 shadow">
                          <CheckIcon className="size-2.5 text-white" strokeWidth={3} />
                        </span>
                      </>
                    )}
                    <button
                      type="button"
                      aria-label="Remove from library"
                      onClick={(event) => {
                        event.stopPropagation()
                        void library.remove(gcs_url)
                      }}
                      className="absolute top-1 left-1 flex size-5 items-center justify-center rounded-full bg-black/70 opacity-0 transition group-hover:opacity-100"
                    >
                      <XMarkIcon className="size-3 text-white" />
                    </button>
                    <button
                      type="button"
                      aria-label="Preview"
                      onClick={(event) => {
                        event.stopPropagation()
                        onPreview(gcs_url)
                      }}
                      className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-black/70 opacity-0 transition group-hover:opacity-100"
                    >
                      <ArrowsPointingOutIcon className="size-2.5 text-white" />
                    </button>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className={`py-3 text-center text-xs ${t.muted}`}>No reference images yet</p>
          )}
        </div>
      ) : (
        <div className="space-y-2 p-3">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-label="Back"
              onClick={() => setMode('main')}
              className={`rounded p-1 transition ${t.icon}`}
            >
              <ArrowLeftIcon className="size-3.5" />
            </button>
            <div className="relative flex-1">
              <input
                autoFocus
                value={library.query}
                onChange={(event) => library.search(event.target.value)}
                placeholder="Search stock photos..."
                className={`w-full rounded-lg py-1.5 pr-8 pl-3 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none ${t.input}`}
              />
              <MagnifyingGlassIcon className={`absolute top-1/2 right-2.5 size-4 -translate-y-1/2 ${t.muted}`} />
            </div>
          </div>

          {library.searching ? (
            <p className={`py-2 text-center text-xs ${t.muted}`}>Searching…</p>
          ) : library.results.length > 0 ? (
            <div className="grid max-h-[180px] grid-cols-3 gap-1.5 overflow-y-auto [scrollbar-width:thin]">
              {library.results.map((image) => (
                <button
                  key={image.url}
                  type="button"
                  disabled={Boolean(library.importing)}
                  title={`Photo by ${image.photographer}`}
                  onClick={() =>
                    void library.importImage(image).then((ok) => {
                      if (ok) {
                        setMode('main')
                        library.resetSearch()
                      }
                    })
                  }
                  className="relative aspect-square overflow-hidden rounded-lg border border-transparent transition hover:border-purple-400 disabled:opacity-50"
                >
                  <img src={image.thumbnail} alt="" className="size-full object-cover" />
                  {library.importing === image.url && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : library.query.trim().length >= MIN_QUERY ? (
            <p className={`py-2 text-center text-xs ${t.muted}`}>No results found</p>
          ) : (
            <p className={`py-2 text-center text-xs ${t.muted}`}>Type at least 3 characters</p>
          )}
        </div>
      )}

      {library.error && <p className="px-3 pb-2 text-[11px] text-red-500">{library.error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) void library.upload(file)
        }}
      />
    </div>,
    document.body,
  )
}
